import { useMemo } from 'react';
import { Icon } from '@iconify/react';
import { Button } from '@/components/shadcn/button';
import { Card } from '@/components/shadcn/card';
import type { PickleballGameListItem, PickleballStandingItem } from '@/services/pickleballService';
import type { FlatPickleballGame } from './pickleballListUtils';
import PickleballMatchCard from './PickleballMatchCard';
import PickleballStandingsTable from './PickleballStandingsTable';
import PickleballStatistikTabs, { type PickleballStatistikTab } from './PickleballStatistikTabs';

interface PickleballStatistikContentProps {
  tab: PickleballStatistikTab;
  onTabChange: (tab: PickleballStatistikTab) => void;
  canEdit: boolean;
  standings: PickleballStandingItem[];
  flatGames: FlatPickleballGame[];
  onTambahGame: () => void;
  onEditScore: (item: FlatPickleballGame) => void;
  onEditPlayer: (game: PickleballGameListItem, gameNumber: number) => void;
  onDelete: (item: FlatPickleballGame) => void;
  onShare: () => void;
  onDownload: () => void;
}

export default function PickleballStatistikContent({
  tab,
  onTabChange,
  canEdit,
  standings,
  flatGames,
  onTambahGame,
  onEditScore,
  onEditPlayer,
  onDelete,
  onShare,
  onDownload,
}: PickleballStatistikContentProps) {
  const normalizedStandings = useMemo(() => {
    return standings.map((item) => {
      const hasPoints = (item.pointsScored ?? 0) > 0 || (item.pointsConceded ?? 0) > 0;
      return {
        ...item,
        gamesPlayed: hasPoints ? item.gamesPlayed : 0,
        winRate: hasPoints ? item.winRate : 0,
        setsWon: hasPoints ? (item.setsWon ?? 0) : 0,
        setsLost: hasPoints ? (item.setsLost ?? 0) : 0,
      };
    });
  }, [standings]);

  return (
    <div className="space-y-4">
      <PickleballStatistikTabs tab={tab} onTabChange={onTabChange} />

      {tab === 'games' && (
        <div className="space-y-3 pb-24">
          {flatGames.length === 0 ? (
            <Card className="rounded-2xl border-0 bg-white px-4 py-10 text-center text-sm text-gray-400 shadow-[inset_0_0_0_1px_#E0E6ED]">
              Belum ada pertandingan pickleball
            </Card>
          ) : (
            flatGames.map((item) => (
              <PickleballMatchCard
                key={item.game.slug}
                game={item.game}
                gameNumber={item.gameNumber}
                canEdit={canEdit}
                onEditScore={() => onEditScore(item)}
                onEditPlayer={() => onEditPlayer(item.game, item.gameNumber)}
                onDelete={() => onDelete(item)}
              />
            ))
          )}
        </div>
      )}

      {tab === 'standings' && (
        <div className="space-y-3 pb-24">
          <PickleballStandingsTable standings={normalizedStandings} />
          <p className="px-1 text-[11px] leading-relaxed text-gray-400">
            M · Main &nbsp; WR · Win rate &nbsp; Set · Set menang-kalah
            <br />
            Poin · Poin dapat-kebobolan
          </p>
        </div>
      )}

      {canEdit && tab === 'games' && (
        <Button
          type="button"
          onClick={onTambahGame}
          className="fixed bottom-[calc(5.5rem+env(safe-area-inset-bottom,0px))] right-4 z-30 h-12 rounded-full bg-[#FC480D] px-5 text-sm font-bold text-white shadow-lg hover:bg-[#D83B08]"
        >
          <Icon icon="mdi:plus" className="h-5 w-5" />
          Tambah Game
        </Button>
      )}

      {tab === 'standings' && (
        <div className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-2 gap-2 bg-white p-4 pb-safe">
          <Button
            type="button"
            variant="secondary"
            onClick={onShare}
            className="h-12 rounded-full bg-[#FFF1EC] text-sm font-bold text-[#FC480D] hover:bg-[#FFE4D8]"
          >
            <Icon icon="mdi:share-variant-outline" className="h-4 w-4" />
            Bagikan
          </Button>
          <Button
            type="button"
            onClick={onDownload}
            className="h-12 rounded-full bg-[#FC480D] text-sm font-bold text-white hover:bg-[#D83B08]"
          >
            <Icon icon="mdi:download" className="h-4 w-4" />
            Download Statistik
          </Button>
        </div>
      )}
    </div>
  );
}
